import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from 'atoms/layout/flex';
import { Skeleton } from 'atoms/loader/Skeleton';
import { Caption } from 'atoms/typography';
import { Bonus } from 'components/collections/Bonus';
import { CardsNumber } from 'components/collections/CardsNumber';
import {
  ProgressBar,
  ProgressBarThreshold,
} from 'components/collections/ProgressBar';
import { Ranking } from 'components/collections/Ranking';
import { Score } from 'components/collections/Score';
import { CollectionsTeamShield } from 'lib/collections';
import { fantasy, glossary } from 'lib/glossary';
import { laptopAndAbove, tabletAndAbove } from 'style/mediaQuery';

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--triple-unit);
  justify-content: space-around;
  align-items: center;
  max-width: 1200px;
  @media ${tabletAndAbove} {
    gap: calc(8 * var(--unit));
    &.noBonus {
      justify-content: flex-start;
    }
  }
`;

const StatBox = styled(Vertical).attrs({ gap: 0, center: true })`
  width: max-content;
  justify-content: stretch;
`;

const ProgressBarWrapper = styled.div`
  min-width: 100%;
  @media ${laptopAndAbove} {
    flex-grow: 1;
    min-width: 0;
  }
`;

const Label = styled(Caption)`
  color: var(--c-static-neutral-600);
  font-weight: var(--t-bold);
`;

type HeaderStatsProps = {
  slotsCount: number;
  fulfilledSlotsCount?: number;
  liveRanking?: number;
  progressThresholds: ProgressBarThreshold[];
  collectionEventStep?: ProgressBarThreshold | null;
  score?: number;
  bonus?: number;
  teamShield?: CollectionsTeamShield;
  showRanking?: boolean;
  showBonus?: boolean;
  loading?: boolean;
};

export const CollectionHeaderStats = ({
  slotsCount,
  fulfilledSlotsCount = 0,
  liveRanking = 0,
  progressThresholds,
  collectionEventStep,
  score = 0,
  bonus = 0,
  teamShield,
  showRanking,
  showBonus,
  loading,
}: HeaderStatsProps) => {
  return (
    <Stats className={classnames({ noBonus: !showBonus })}>
      <StatBox>
        <Label>
          <FormattedMessage {...glossary.cards} />
        </Label>
        <Skeleton loading={loading}>
          <CardsNumber
            ownedCards={fulfilledSlotsCount}
            totalCards={slotsCount}
          />
        </Skeleton>
      </StatBox>
      {showRanking && (
        <StatBox>
          <Label>
            <FormattedMessage {...fantasy.rank} />
          </Label>
          <Skeleton loading={loading}>
            <Ranking liveRanking={liveRanking} />
          </Skeleton>
        </StatBox>
      )}
      <StatBox>
        <Label>
          <FormattedMessage {...fantasy.score} />
        </Label>
        <Skeleton loading={loading}>
          <Score score={score} />
        </Skeleton>
      </StatBox>
      {showBonus && (
        <>
          <StatBox>
            <Label>
              <FormattedMessage {...fantasy.bonus} />
            </Label>
            <Skeleton loading={loading}>
              <Bonus bonus={bonus} />
            </Skeleton>
          </StatBox>
          <ProgressBarWrapper>
            <ProgressBar
              thresholds={progressThresholds}
              collectionEventStep={collectionEventStep}
              score={score}
              teamShield={teamShield}
            />
          </ProgressBarWrapper>
        </>
      )}
    </Stats>
  );
};
